import { MapPin, Phone, Mail, Copy, Navigation } from "lucide-react";
import { toast } from "sonner";
import { useCountdown, pad2 } from "../../hooks/useCountdown";
import { AddToCalendar } from "./AddToCalendar";

const Unit = ({ v, label }) => (
  <div className="text-center">
    <div className="font-display text-5xl sm:text-6xl leading-none text-white tabular-nums">{pad2(v)}</div>
    <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)] mt-1">{label}</div>
  </div>
);

export const NextSessionBanner = ({ season }) => {
  const next = season?.next_session;
  const cd = useCountdown(next?.start);
  if (!next || !cd) return null;
  const dateLabel = new Date(next.start).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div className="rounded-2xl border border-[color:var(--border)] p-6 sm:p-8 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${next.color}22, transparent 60%), var(--bg-elev)` }} data-testid="next-session-banner">
      <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-2 flex items-center gap-2"><span className="live-dot" /> {season.label} · C'est lancé</div>
      <h2 className="font-display text-4xl sm:text-5xl leading-none mb-6">Prochaine séance à {/^[aeiouyéèê]/i.test(season.venue_name || "") ? `l'${season.venue_name}` : (season.venue_name || "l'Espace Carco")}</h2>
      <div className="grid grid-cols-4 gap-3 max-w-md mb-5" data-testid="next-session-countdown">
        <Unit v={cd.days} label="jours" /><Unit v={cd.hours} label="heures" /><Unit v={cd.minutes} label="minutes" /><Unit v={cd.seconds} label="secondes" />
      </div>
      <div className="text-sm text-[color:var(--text)] capitalize" data-testid="next-session-label">
        {dateLabel} · <span style={{ color: next.color }}>{next.course_name}</span> — {next.slot?.start}, {next.room}
        <span className="normal-case text-[color:var(--text-muted)]"> · {next.slot?.level}{next.slot?.age ? ` (${next.slot.age})` : ""}</span>
      </div>
      <div className="mt-4">
        <AddToCalendar
          label="Ajouter à mon agenda"
          date={next.date}
          season={season}
          session={{ course_id: next.course_id, course_name: next.course_name, room: next.room, start: next.slot?.start, end: next.slot?.end, level: next.slot?.level, age: next.slot?.age }}
        />
      </div>
    </div>
  );
};

export const VenueSection = ({ season }) => {
  if (!season) return null;
  const address = `${season.venue_name} · ${season.address} · ${season.city}`;
  const copy = () => navigator.clipboard.writeText(`${season.address}, ${season.city}`).then(() => toast.success("Adresse copiée")).catch(() => toast.error("Impossible de copier"));
  return (
    <div className="glass rounded-2xl p-6 sm:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-start" data-testid="venue-section">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-2 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Horaires & lieu</div>
        <h3 className="font-display text-4xl leading-none">{season.venue_name}</h3>
        <p className="text-sm text-[color:var(--text-muted)] mt-2">{season.address}<br />{season.city}</p>
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          {(season.phones || []).map(p => (
            <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors"><Phone className="w-3 h-3" /> {p}</a>
          ))}
          {season.email && <a href={`mailto:${season.email}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors"><Mail className="w-3 h-3" /> {season.email}</a>}
        </div>
        <p className="text-[10px] text-[color:var(--text-muted)] mt-4">Saison du {new Date(season.start_date).toLocaleDateString("fr-FR", { dateStyle: "long" })} au {new Date(season.end_date).toLocaleDateString("fr-FR", { dateStyle: "long" })}.</p>
      </div>
      <div className="flex md:flex-col gap-2">
        <button type="button" onClick={copy} data-testid="venue-copy" title={address} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--border)] hover:border-white text-[10px] uppercase tracking-widest transition-colors"><Copy className="w-3.5 h-3.5" /> Copier l'adresse</button>
        {season.maps_url && <a href={season.maps_url} target="_blank" rel="noopener noreferrer" data-testid="venue-maps" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--accent)] text-black font-semibold text-[10px] uppercase tracking-widest hover:brightness-110 transition"><Navigation className="w-3.5 h-3.5" /> Itinéraire</a>}
      </div>
    </div>
  );
};
